import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  FolderKanban, 
  Wrench, 
  User, 
  LogOut, 
  Shield,
  ChevronDown,
  Home,
  BookOpen,
  History,
  Users,
  KeyRound,
  Mail,
  HelpCircle
} from 'lucide-react';
import ChangePasswordModal from '../components/ChangePasswordModal';
import HelpPanel from '../pages/HelpPanel';

const PROJECT_LOGO = "/assets/favicon-icon.png";

// Custom icon component for Product Configuration using uploaded image
const ProductConfigIcon = ({ className }) => (
  <img src="/assets/product-config-icon.png" alt="" className={`${className} object-contain`} />
);

const DashboardLayout = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const contactSubject = encodeURIComponent(
    `MySpecWorkspace | ${user?.company || 'N/A'} | ${user?.first_name || 'User'}`
  );
  const contactHref = `mailto:info@timconsulting.com.au?subject=${contactSubject}`;

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: Home, end: true },
    ...(!isAdmin ? [
      { to: '/dashboard/projects', label: 'Projects', icon: FolderKanban },
      { to: '/dashboard/team', label: 'Team', icon: Users },
    ] : []),
    { to: '/dashboard/tools', label: 'Tools', icon: Wrench },
    { to: '/dashboard/resources', label: 'Resources', icon: BookOpen },
    ...(isAdmin || user?.is_primary ? [{ to: '/dashboard/history', label: 'History', icon: History }] : []),
    ...(isAdmin ? [{ to: '/dashboard/users', label: 'Users', icon: Users }] : []),
    ...(isAdmin ? [{ to: '/dashboard/product-config', label: 'Product Config', icon: null }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo & Brand */}
            <div className="flex items-center gap-4">
              <img 
                src={PROJECT_LOGO}
                alt="S1000D Project"
                className="w-10 h-10 object-contain"
              />
              <span 
                className="text-xl font-bold text-slate-900 hidden sm:block"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                MySpecWorkspace
              </span>
            </div>

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`
                  }
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.icon && <link.icon className="w-4 h-4" />}
                  <span className={link.icon ? "hidden md:inline" : ""}>{link.label}</span>
                </NavLink>
              ))}
            </nav>

            {/* Help & User Menu */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowHelp(true)}
                className="text-slate-500 hover:text-purple-700 hover:bg-purple-50 transition-colors"
                data-testid="help-btn"
                title="User Guide"
              >
                <HelpCircle className="w-5 h-5" />
              </Button>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 text-slate-700"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white font-medium">
                    {user?.first_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden md:inline">{user?.first_name}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.first_name} {user?.last_name}</span>
                    <span className="text-xs text-slate-500">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => navigate('/dashboard/settings')}
                  data-testid="settings-menu-item"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setShowPasswordModal(true)}
                  data-testid="change-password-menu-item"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/dashboard/security')}
                  data-testid="security-menu-item"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Security & MFA
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="contact-tim-menu-item">
                  <a href={contactHref} className="flex items-center cursor-pointer">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact TIM Consulting
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600"
                  data-testid="logout-menu-item"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4 md:px-8">
        <Outlet />
      </main>
      <ChangePasswordModal open={showPasswordModal} onClose={() => setShowPasswordModal(false)} token={token} />
      {showHelp && <HelpPanel onClose={() => setShowHelp(false)} currentPath={location.pathname} />}
    </div>
  );
};

export default DashboardLayout;
